import { useState } from 'react';

import * as Dialog from '@radix-ui/react-dialog';
import { Phone, X } from 'lucide-react';

import { Button } from '@/components/ui/button';

interface TestCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  configurationName: string;
  onSubmit: (phoneNumber: string) => void;
  isLoading?: boolean;
}

export const TestCallDialog = ({
  open,
  onOpenChange,
  configurationName,
  onSubmit,
  isLoading = false,
}: TestCallDialogProps) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [error, setError] = useState<string>('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const trimmedPhone = phoneNumber.trim();

    // Require international (E.164-like) format with a leading '+'
    if (!trimmedPhone.startsWith('+')) {
      setError(
        'Please enter a phone number in international format, starting with + and country code (e.g. +441234567890).',
      );
      return;
    }

    // Basic phone number validation (digits only, ignoring the leading '+')
    const cleanedPhone = trimmedPhone.replace(/\D/g, '');
    if (cleanedPhone.length < 10) {
      setError('Please enter a valid phone number');
      return;
    }

    // Use the user-provided E.164-style number, stripped of any non-digit/non-plus characters
    const formattedPhone = trimmedPhone.replace(/[^\d+]/g, '');

    onSubmit(formattedPhone);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && !isLoading) {
      setPhoneNumber('');
      setError('');
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" />
        <Dialog.Content className="bg-background fixed top-1/2 left-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg p-6 shadow-lg">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <Dialog.Title className="text-lg font-semibold">Test Configuration Call</Dialog.Title>
              <Dialog.Description className="text-muted-foreground mt-2 text-sm">
                Initiate a test call to verify the &ldquo;{configurationName}&rdquo; configuration
                works as expected.
              </Dialog.Description>
              <Dialog.Close asChild>
                <button
                  type="button"
                  className="text-muted-foreground hover:text-foreground absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 disabled:pointer-events-none"
                  disabled={isLoading}
                  onClick={() => {
                    setPhoneNumber('');
                    setError('');
                    onOpenChange(false);
                  }}
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </button>
              </Dialog.Close>
            </div>
            <div className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="phoneNumber" className="text-sm font-medium">
                  Phone Number
                </label>
                <input
                  id="phoneNumber"
                  type="tel"
                  placeholder="+1234567890"
                  value={phoneNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPhoneNumber(e.target.value);
                    setError('');
                  }}
                  disabled={isLoading}
                  required
                  className="border-input placeholder:text-muted-foreground focus-visible:ring-ring flex h-10 w-full rounded-md border bg-transparent px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                />
                {error && <p className="text-destructive text-sm">{error}</p>}
                <p className="text-muted-foreground text-xs">
                  Enter the phone number in E.164 format (e.g., +1234567890)
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setPhoneNumber('');
                  setError('');
                  onOpenChange(false);
                }}
                disabled={isLoading}
              >
                <X className="h-4 w-4" />
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Calling...
                  </>
                ) : (
                  <>
                    <Phone className="mr-2 h-4 w-4" />
                    Start Test Call
                  </>
                )}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
